import {
  type ContentDeleteImpact,
  ContentDeleteImpactSchema,
  apiPath,
  type BulkPlacementRequest,
  type ContentRef,
  type ContentUsage,
  ContentUsageSchema,
  type GroupContent,
  GroupContentSchema,
  type Placement,
  PlacementSchema,
  type PlayerContent,
  PlayerContentSchema,
  PublishedCountsSchema,
  type PublishRequest,
  type Restriction,
  type PublishResult,
  PublishResultSchema,
  type UpdatePlacementRequest,
} from '@codot/contracts';
import { z } from 'zod';
import { request } from './http';

/** Phase 16 publishing (legacy CODOT model): each player's own ordered list. */
export const publishingApi = {
  publish: (body: PublishRequest): Promise<PublishResult> =>
    request('POST', apiPath('publish'), PublishResultSchema, body),
  playerContent: (screenId: string): Promise<PlayerContent> =>
    request('GET', apiPath('playerContent', { screenId }), PlayerContentSchema),
  setLoop: (screenId: string, loop: boolean): Promise<PlayerContent> =>
    request('PATCH', apiPath('playerContent', { screenId }), PlayerContentSchema, { loop }),
  reorderPlayer: (screenId: string, ids: string[]): Promise<PlayerContent> =>
    request('PUT', apiPath('playerContentOrder', { screenId }), PlayerContentSchema, { ids }),
  groupContent: (groupId: string): Promise<GroupContent> =>
    request('GET', apiPath('groupContent', { groupId }), GroupContentSchema),
  reorderGroup: (groupId: string, ids: string[]): Promise<GroupContent> =>
    request('PUT', apiPath('groupContentOrder', { groupId }), GroupContentSchema, { ids }),
  update: (placementId: string, body: UpdatePlacementRequest): Promise<Placement> =>
    request('PATCH', apiPath('placement', { placementId }), PlacementSchema, body),
  unpublish: (placementId: string): Promise<unknown> => request('DELETE', apiPath('placement', { placementId }), null),
  bulk: (body: BulkPlacementRequest): Promise<unknown> => request('POST', apiPath('placementsBulk'), null, body),
  /** Phase 17.5: the content's own restriction (mode 'none' removes it). */
  setContentRestriction: (content: ContentRef, restriction: Restriction): Promise<unknown> =>
    request('PUT', apiPath('contentRestriction'), null, { content, restriction }),
  /** The mute switch on a video, campaign or playlist (every player it plays on). */
  setContentMuted: (content: ContentRef, muted: boolean): Promise<unknown> => request('PUT', apiPath('contentMute'), null, { content, muted }),
  /** Phase 17.2: unpublish a tag publication from every player it reached. */
  unpublishTag: (publicationId: string): Promise<unknown> => request('DELETE', apiPath('tagPublication', { publicationId }), null),
  /** Phase 17.2: bring back a group/tag item this player had removed. */
  restore: (exclusionId: string): Promise<unknown> => request('DELETE', apiPath('placementExclusion', { exclusionId }), null),
  setScreenTags: (screenId: string, tags: string[]): Promise<unknown> =>
    request('PUT', apiPath('screenTags', { screenId }), null, { tags }),
  setGroupTags: (groupId: string, tags: string[]): Promise<unknown> =>
    request('PUT', apiPath('groupTags', { groupId }), null, { tags }),
  tags: (): Promise<string[]> => request('GET', apiPath('tags'), z.array(z.string())),
  /** Everything a delete would touch, and which published campaigns/playlists it would leave empty. */
  deleteImpact: (c: ContentRef): Promise<ContentDeleteImpact> =>
    request('GET', `${apiPath('contentDeleteImpact')}?kind=${c.kind}&id=${c.id}`, ContentDeleteImpactSchema),
  usage: (c: ContentRef): Promise<ContentUsage> =>
    request('GET', `${apiPath('contentUsage')}?kind=${c.kind}&id=${c.id}`, ContentUsageSchema),
  publishedCounts: (): Promise<Record<string, number>> =>
    request('GET', apiPath('publishedCounts'), PublishedCountsSchema),
};
